import { useEffect, useMemo, useState } from "react";
import { monthLabel } from "../lib/api";
import { useTaxonomy } from "../lib/useTaxonomy";

const selectCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const Stat = ({ label, value, testid, accent }) => (
  <div className="bg-[#0A0A0A] p-5" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{label}</p>
    <p className={`font-heading text-3xl font-black ${accent || "text-white"}`}>{value}</p>
  </div>
);

export const SessionsPanel = ({ sessions, month, onOpen, formatDate }) => {
  const { centreForBatch } = useTaxonomy();
  const [centre, setCentre] = useState("");
  const [batch, setBatch] = useState("");

  const centres = useMemo(() => [...new Set(sessions.map((s) => centreForBatch(s.batch)).filter(Boolean))].sort(), [sessions, centreForBatch]);
  const batches = useMemo(() => [...new Set(sessions.filter((s) => !centre || centreForBatch(s.batch) === centre).map((s) => s.batch))].sort(), [sessions, centre, centreForBatch]);
  useEffect(() => {
    if (centre && !centres.includes(centre)) setCentre("");
    if (batch && !batches.includes(batch)) setBatch("");
  }, [centres, batches, centre, batch]);
  const filtered = sessions.filter((s) => (!centre || centreForBatch(s.batch) === centre) && (!batch || s.batch === batch));

  const present = filtered.reduce((a, s) => a + s.present, 0);
  const marked = filtered.reduce((a, s) => a + s.present + s.absent, 0);
  const avg = marked ? Math.round((present / marked) * 100) : null;
  const scope = batch || centre || "All Centres";

  return (
    <div data-testid="sessions-panel">
      <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Sessions Held in {monthLabel(month)}</h2>
      <div className="flex flex-wrap gap-3 mb-4">
        <select value={centre} onChange={(e) => { setCentre(e.target.value); setBatch(""); }} className={selectCls} data-testid="session-centre-filter">
          <option value="">All Centres</option>
          {centres.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={batch} onChange={(e) => setBatch(e.target.value)} className={selectCls} data-testid="session-batch-filter">
          <option value="">All Batches</option>
          {batches.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-4" data-testid="session-stats">
        <Stat label={`Sessions · ${scope}`} value={filtered.length} testid="stat-sessions" />
        <Stat label="Avg. Present" value={avg === null ? "—" : `${avg}%`} accent="text-[#D4AF37]" testid="stat-avg-present" />
        <Stat label="Player Attendances" value={present} testid="stat-present-total" />
      </div>
      <p className="text-white/30 text-[11px] mb-4">Average present % counts enrolled players only (present ÷ present + absent). Trial kids are excluded.</p>
      <div className="border border-white/10" data-testid="sessions-list">
        {filtered.length === 0 && <p className="text-white/40 text-sm p-6 bg-[#0A0A0A]">No sessions recorded in {monthLabel(month)} for {scope} yet.</p>}
        {filtered.map((s) => {
          const pct = s.present + s.absent ? Math.round((s.present / (s.present + s.absent)) * 100) : null;
          return (
            <button key={`${s.date}-${s.batch}`} onClick={() => onOpen(s)}
              className="w-full flex flex-wrap items-center justify-between gap-3 border-b border-white/5 last:border-0 p-4 bg-[#0A0A0A] hover:bg-[#0F0F0F] transition-colors duration-150 text-left"
              data-testid={`session-${s.date}-${s.batch.replace(/[^a-z0-9]/gi, "-")}`}>
              <div>
                <p className="font-semibold text-sm" data-testid={`session-date-${s.date}`}>{formatDate(s.date)}</p>
                <p className="text-white/40 text-xs mt-0.5">{s.batch}{s.coach ? ` · Coach ${s.coach}` : ""}</p>
              </div>
              <div className="flex gap-4 text-xs">
                <span className="text-[#D4AF37] font-semibold">{s.present} present</span>
                <span className="text-red-400">{s.absent} absent</span>
                {s.trial > 0 && <span className="text-blue-400">{s.trial} trial</span>}
                {pct !== null && <span className="text-white/60">{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
